import type { UserUuid } from "../identity";
// src/types/healthcare/ClinikoImportTypes.ts

/**
 * The Cliniko → Xoe MIGRATION run (S13.1/S14, ClickUp 86d418zae / 86d418ur4).
 *
 * ⚠️ **This is not the ongoing Cliniko sync.** `ClinikoSyncApi` mirrors an account the clinic is
 * still working in; these types describe the ONE-SHOT import that moves a clinic off Cliniko.
 * The two share importers server-side and share nothing here.
 */

/** A run is either a rehearsal or the real thing. Resume NEVER changes a run's mode. */
export type ClinikoImportRunMode = "dry_run" | "live";

/**
 * `pending` and `running` are the ACTIVE pair — see `CLINIKO_IMPORT_ACTIVE_STATUSES`.
 *
 * ⚠️ `partial` is a real terminal state, not a transient one: the run finished and at least one
 * section failed or was stepped over. It is resumable; `completed` is not.
 */
export type ClinikoImportRunStatus = "pending" | "running" | "completed" | "partial" | "failed";

/**
 * One section's outcome, as `ClinikoImportRun::recordSectionResult()` stores it.
 *
 * ⚠️ **`success: true` DOES NOT MEAN THE SECTION RAN.** A section a dry run cannot rehearse, and a
 * registered section whose importer has not landed yet, are both recorded as successes carrying a
 * `skipped` reason — so that a skip does not inflate `sections_failed`. Render `skipped` honestly;
 * a green tick against `skipped_dry_run_unsupported` is exactly the wrong assurance to give before
 * a one-shot migration of a live clinic.
 */
export interface SectionResult {
	success: boolean;
	count: number;
	error: string | null;
	/** Operator-facing warning CODES (`credit_notes_not_imported:12`), never clinical content. */
	warnings?: string[];
	/** The section's own summary counters. Counts and ids only. */
	counts?: Record<string, number | string | boolean | null>;
	/** `skipped_dry_run_unsupported` | `skipped_not_implemented`, or absent. */
	skipped?: string;
}

/** A row of `cliniko_import_runs`, as `ClinikoImportRunController::format()` serialises it. */
export interface ClinikoImportRun {
	run_uuid: string;
	mode: ClinikoImportRunMode;
	status: ClinikoImportRunStatus;
	/** The section the run is inside right now. NULL between sections and once it ends. */
	current_section: string | null;
	/**
	 * The registry keys in EXECUTION order, sent by the server so the UI never has to guess.
	 * `CLINIKO_IMPORT_SECTIONS` below is the fallback and the source of the human labels.
	 */
	section_order?: string[];
	sections_total: number;
	sections_completed: number;
	sections_failed: number;
	section_results: Record<string, SectionResult> | null;
	error_summary: string | null;
	initiated_by_user_id: UserUuid | null;
	started_at: string | null;
	completed_at: string | null;
	created_at: string | null;
}

export interface ClinikoImportRunListResponse {
	data: ClinikoImportRun[];
	total: number;
	page: number;
}

/** The 202 from `POST import-runs` and `POST import-runs/{uuid}/resume`. */
export interface ClinikoImportRunStartResponse {
	run_uuid: string;
	status: ClinikoImportRunStatus;
	mode?: ClinikoImportRunMode;
	message?: string;
}

/**
 * The 409 (a run is already pending/running, or a `completed` run cannot be resumed) and the 400
 * (Cliniko is not configured).
 *
 * ⚠️ `run_uuid` is the ACTIVE run on an `already_running` conflict — the caller's obvious next
 * move (watch that one) is in the response, so this is a redirect, not an error.
 */
export interface ClinikoImportRunConflict {
	success: false;
	message: string;
	reason: "already_running" | "already_completed" | "not_configured";
	run_uuid?: string | null;
}

/** The two statuses that mean a run is still going — the poll runs exactly while one holds. */
export const CLINIKO_IMPORT_ACTIVE_STATUSES: ClinikoImportRunStatus[] = ["pending", "running"];

/**
 * The nine sections, in EXECUTION order.
 *
 * ⚠️ **THE ORDER IS THE CORRECTNESS ARGUMENT, NOT A LAYOUT CHOICE** — it mirrors
 * `ClinikoImportOrchestrator::SECTIONS`, which `ClinikoImportSectionRegistryTest` pins as a
 * literal. Practitioners run before patients so unmatched clinicians surface in time for an
 * operator to create the seats and RESUME; products before invoices so order lines resolve;
 * appointments last because it seeds the cursor the hourly sync then takes over.
 *
 * Used for the labels and as the fallback when a run row carries no `section_order`.
 */
export const CLINIKO_IMPORT_SECTIONS: { key: string; label: string }[] = [
	{ key: "reference", label: "Reference data" },
	{ key: "practitioners", label: "Practitioners" },
	{ key: "patients", label: "Patients" },
	{ key: "clinical", label: "Clinical records" },
	{ key: "documents", label: "Documents" },
	{ key: "products", label: "Products" },
	{ key: "invoices", label: "Invoices" },
	{ key: "comms", label: "Communications" },
	{ key: "appointments", label: "Appointments" },
];
